import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import { createMissingBox, uploadFilesToMissingBox } from "../../services/missing-box-partner.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { readBranches } from "../../services/branch.service.ts";
import { autocompletePartners, fetchPartner } from "../../services/partner.service.ts";
import S3Service from "../../services/s3.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import dayjs from "dayjs";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";
import "dayjs/locale/es";
import { getCurrencyFormat, parseCurrency } from "../../utils/index.ts";
dayjs.locale("es");

const getMonthEnd = (d: any) => dayjs(d).endOf("month").date();
const normalizeStartToFortnight = (d: any) => {
  const date = dayjs(d);
  const day = date.date();
  return day <= 15 ? date.startOf("month") : date.date(16);
};
const normalizeEndToFortnight = (d: any) => {
  const date = dayjs(d);
  const day = date.date();
  return day <= 15 ? date.date(15) : date.date(getMonthEnd(date));
};
const countFortnights = (start: any, end: any) => {
  let s = normalizeStartToFortnight(start);
  const e = normalizeEndToFortnight(end);
  if (s.isAfter(e, "day")) return 0;
  let count = 0;
  while (!s.isAfter(e, "day")) {
    const currentEnd = s.date() <= 15 ? s.date(15) : s.date(getMonthEnd(s));
    count += 1;
    s = currentEnd.add(1, "day");
    s = normalizeStartToFortnight(s);
  }
  return count;
};

const SectionTitle = ({ icon, children }: { icon: string; children: React.ReactNode }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "1rem 1.25rem", borderBottom: "1px solid #f3f4f6",
  }}>
    <i className={`fa-solid ${icon}`} style={{ fontSize: 13, color: "#6b7280" }} />
    <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{children}</span>
  </div>
);

const FieldLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5, display: "block" }}>
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span>}
  </label>
);

const FieldError = ({ msg }: { msg?: string }) =>
  msg ? <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{msg}</div> : null;

const NewMissingBox = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const [profileUrl, setProfileUrl] = useState<string>("");
  const [fortnightsCount, setFortnightsCount] = useState<number>(0);
  const [totalInput, setTotalInput] = useState<string>("");
  const [isEditingTotal, setIsEditingTotal] = useState<boolean>(false);
  const [searchPartner, setSearchPartner] = useState<string>("");
  const [filteredPartners, setFilteredPartners] = useState<any[]>([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  useAppSettings();

  let timeoutId: any;

  const validationSchema = Yup.object().shape({
    branchId: Yup.number().required("Sucursal requerida"),
    partnerId: Yup.number().required("Colaborador requerido"),
    incidentDate: Yup.number().required("Fecha requerida"),
    totalMissingAmount: Yup.number().positive().required("Monto requerido"),
    amountPerPeriod: Yup.number().positive().required("Monto requerido"),
    startPeriodDate: Yup.number().required("Inicio requerido"),
    endPeriodDate: Yup.number().required("Fin requerido"),
    note: Yup.string().optional(),
  });

  const formik = useFormik({
    validationSchema,
    initialValues: {
      branchId: "",
      partnerId: "",
      incidentDate: null,
      totalMissingAmount: "",
      amountPerPeriod: "",
      startPeriodDate: null,
      endPeriodDate: null,
      note: "",
      missingBoxFiles: [] as File[],
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const created = await createMissingBox({
          branchId: Number(values.branchId),
          partnerId: Number(values.partnerId),
          incidentDate: values.incidentDate as unknown as number,
          totalMissingAmount: Number(values.totalMissingAmount),
          amountPerPeriod: Number(values.amountPerPeriod),
          startPeriodDate: values.startPeriodDate as unknown as number,
          endPeriodDate: values.endPeriodDate as unknown as number,
          note: values.note || undefined,
          fortnights: fortnightsCount || 0,
        });
        if (created?.id && (values.missingBoxFiles?.length || 0) > 0) {
          try {
            await uploadFilesToMissingBox(created.id, values.missingBoxFiles);
          } catch (err) {
            console.error("Error subiendo archivos:", err);
            notification("Advertencia", "Faltante creado, pero algunos archivos no se pudieron subir", "warning");
          }
        }
        notification("Éxito", "Faltante en caja creado", "success");
        navigate("/faltantes-caja");
      } catch (e: any) {
        notification("Error", e?.message || "No se pudo crear", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const handlePartnerSearch = async (searchTerm: string) => {
    setSearchPartner(searchTerm);
    setShowPartnerDropdown(true);
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const data = await autocompletePartners(searchTerm);
        setFilteredPartners(data);
      } catch (error) {
        console.error("Error searching partners:", error);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  };

  const handlePartnerSelect = async (partner: any) => {
    formik.setFieldValue("partnerId", partner.id);
    setSearchPartner(`${partner.person.firstName} ${partner.person.lastName}`);
    setShowPartnerDropdown(false);
    await loadPartnerInfo(partner.id);
  };

  useEffect(() => {
    readBranches().then(setBranches).catch(() => setBranches([]));
    autocompletePartners("").then(setFilteredPartners).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditingTotal) {
      const val = formik.values.totalMissingAmount;
      setTotalInput(val === null || val === undefined || val === "" ? "" : getCurrencyFormat(Number(val)));
    }
  }, [formik.values.totalMissingAmount, isEditingTotal]);

  const recalcAmountPerPeriod = (totalStr: string | number, count: number) => {
    const total = Number(totalStr);
    if (!count || !isFinite(total) || total <= 0) {
      formik.setFieldValue("amountPerPeriod", "");
      return;
    }
    formik.setFieldValue("amountPerPeriod", (total / count).toFixed(2));
  };

  const displayCurrency = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return !isFinite(num) ? "" : getCurrencyFormat(num);
  };

  useEffect(() => {
    recalcAmountPerPeriod(formik.values.totalMissingAmount, fortnightsCount);
  }, [formik.values.totalMissingAmount, fortnightsCount]);

  const loadPartnerInfo = async (partnerId: number) => {
    try {
      setLoading(true);
      const partner = await fetchPartner(partnerId);
      setSelectedPartner(partner);
      const curp = partner?.person?.curp;
      if (curp) {
        try {
          const file = await S3Service.getFile("PERFIL_" + curp);
          setProfileUrl(file?.url || "");
        } catch {
          setProfileUrl("");
        }
      } else {
        setProfileUrl("");
      }
    } catch {
      setSelectedPartner(null);
      setProfileUrl("");
    } finally {
      setLoading(false);
    }
  };

  const errorFor = (name: string) => {
    const touched = formik.touched[name as any];
    const error = formik.errors[name as any] as any;
    return touched && error ? <FieldError msg={error} /> : null;
  };

  const handleIncidentDateChange = (date: any) => {
    formik.setFieldValue("incidentDate", date ? date.unix() : null);
  };

  const handlePeriodDateChange = (range: any) => {
    const [start, end] = range || [];
    if (!start || !end) {
      setFortnightsCount(0);
      formik.setFieldValue("startPeriodDate", null);
      formik.setFieldValue("endPeriodDate", null);
      return;
    }
    const ns = normalizeStartToFortnight(start);
    const ne = normalizeEndToFortnight(end);
    const count = countFortnights(ns, ne);
    setFortnightsCount(count);
    formik.setFieldValue("startPeriodDate", ns.unix());
    formik.setFieldValue("endPeriodDate", ne.unix());
    recalcAmountPerPeriod(formik.values.totalMissingAmount, count);
  };

  const addFiles = (files: File[]) => {
    const updated = [...(formik.values.missingBoxFiles || []), ...files];
    formik.setFieldValue("missingBoxFiles", updated);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "0.55rem 0.85rem",
    border: "1px solid #e5e7eb", borderRadius: 8,
    fontSize: 13, color: "#111827", background: "#fff",
    outline: "none",
  };

  return (
    <Layout>
      <Title baseTitle="Faltantes en caja" basePath="/faltantes-caja" title="Nuevo faltante en caja" />

      <div style={{ padding: "1rem", display: "grid", gridTemplateColumns: "280px 1fr", gap: "1.25rem", alignItems: "stretch" }}
        className="new-missing-box-grid">

        {/* ── Left: Partner card ── */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
          <SectionTitle icon="fa-user">Colaborador</SectionTitle>
          <div style={{ padding: "1.5rem", textAlign: "center" }}>
            {selectedPartner ? (
              <>
                <img
                  src={profileUrl || "/assets/img/avatar.jpg"}
                  alt="Foto"
                  onError={(e: any) => { e.target.src = "/assets/img/avatar.jpg"; }}
                  style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", border: "3px solid #e5e7eb", marginBottom: "1rem" }}
                />
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
                  {toCamelCase(`${selectedPartner?.person?.firstName || ""} ${selectedPartner?.person?.lastName || ""} ${selectedPartner?.person?.secondLastName || ""}`)}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: "1rem" }}>
                  {selectedPartner?.candidate?.vacancy?.position?.name || ""}
                </div>
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "0.65rem", border: "1px solid #e5e7eb", textAlign: "left", marginBottom: "1rem" }}>
                  <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", marginBottom: 2 }}>Sucursal</div>
                  <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{selectedPartner?.candidate?.branch?.name || "-"}</div>
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
                  borderRadius: 8, padding: "0.45rem 0.85rem", fontSize: 12, fontWeight: 500,
                }}>
                  <i className="fa-solid fa-bell" style={{ fontSize: 10 }} />
                  Se notificará al guardar
                </div>
              </>
            ) : (
              <>
                <div style={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: "#f3f4f6", border: "3px solid #e5e7eb",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 1rem",
                }}>
                  <i className="fa-solid fa-user" style={{ fontSize: 28, color: "#d1d5db" }} />
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: "1rem" }}>
                  Selecciona un colaborador para ver su información
                </div>
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "0.65rem 0.85rem", fontSize: 12, color: "#92400e", textAlign: "left" }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
                  El monto por quincena se calcula automáticamente al seleccionar el periodo
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Right: Form ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

          {/* Datos principales */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
            <SectionTitle icon="fa-file-invoice-dollar">Datos del faltante</SectionTitle>
            <div style={{ padding: "1.25rem" }}>
              <ConfigProvider locale={esES}>
                <div className="row g-3">

                  {/* Colaborador search */}
                  <div className="col-md-6">
                    <FieldLabel required>Colaborador</FieldLabel>
                    <div style={{ position: "relative" }}>
                      <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", zIndex: 1 }}>
                        {searchLoading
                          ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 12 }} />
                          : <i className="fa fa-search" style={{ fontSize: 12 }} />}
                      </span>
                      <input
                        type="text"
                        style={{ ...inputStyle, paddingLeft: "2rem" }}
                        placeholder="Buscar colaborador..."
                        value={searchPartner}
                        onChange={(e) => handlePartnerSearch(e.target.value)}
                        onFocus={() => setShowPartnerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowPartnerDropdown(false), 200)}
                      />
                      {showPartnerDropdown && filteredPartners.length > 0 && (
                        <div style={{
                          position: "absolute", top: "100%", left: 0, right: 0,
                          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)", zIndex: 1000,
                          maxHeight: 200, overflowY: "auto", marginTop: 4,
                        }}>
                          {filteredPartners.map((partner) => (
                            <div
                              key={partner.id}
                              style={{ padding: "0.6rem 0.85rem", cursor: "pointer", fontSize: 13, color: "#374151" }}
                              onClick={() => handlePartnerSelect(partner)}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#f8fafc"; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "#fff"; }}
                            >
                              {partner.person.firstName} {partner.person.lastName}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {errorFor("partnerId")}
                  </div>

                  {/* Sucursal */}
                  <div className="col-md-6">
                    <FieldLabel required>Sucursal</FieldLabel>
                    <select
                      style={inputStyle}
                      name="branchId"
                      value={formik.values.branchId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Selecciona una sucursal</option>
                      {branches?.map((b: any) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    {errorFor("branchId")}
                  </div>

                  {/* Fecha incidencia */}
                  <div className="col-md-4">
                    <FieldLabel required>Fecha de incidencia</FieldLabel>
                    <DatePicker
                      style={{ width: "100%", borderRadius: 8 }}
                      format="DD/MM/YYYY"
                      value={formik.values.incidentDate ? dayjs.unix(formik.values.incidentDate) : null}
                      onChange={handleIncidentDateChange}
                    />
                    {errorFor("incidentDate")}
                  </div>

                  {/* Periodo descuento */}
                  <div className="col-md-8">
                    <FieldLabel required>Periodo de descuento</FieldLabel>
                    <DatePicker.RangePicker
                      style={{ width: "100%", borderRadius: 8 }}
                      format="DD/MM/YYYY"
                      value={
                        formik.values.startPeriodDate && formik.values.endPeriodDate
                          ? [dayjs.unix(formik.values.startPeriodDate), dayjs.unix(formik.values.endPeriodDate)]
                          : null
                      }
                      renderExtraFooter={() => (
                        <div style={{ fontSize: 11, color: "#9ca3af", padding: "0.25rem 0" }}>
                          <i className="fa fa-info-circle" style={{ marginRight: 5 }} />
                          Selecciona fechas quincenales
                        </div>
                      )}
                      onChange={handlePeriodDateChange}
                    />
                    {errorFor("startPeriodDate")}
                    {errorFor("endPeriodDate")}
                  </div>

                  {/* Monto total */}
                  <div className="col-md-4">
                    <FieldLabel required>Monto total</FieldLabel>
                    <input
                      type="text"
                      style={inputStyle}
                      placeholder="$0.00"
                      value={isEditingTotal ? totalInput : displayCurrency(formik.values.totalMissingAmount)}
                      onFocus={() => {
                        setIsEditingTotal(true);
                        const current = formik.values.totalMissingAmount;
                        setTotalInput(current === null || current === undefined || current === "" ? "" : String(current));
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTotalInput(val);
                        const parsed = parseCurrency(val || "");
                        formik.setFieldValue("totalMissingAmount", isNaN(parsed) ? "" : parsed);
                      }}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        setIsEditingTotal(false);
                        const parsed = parseCurrency(totalInput || "");
                        if (isNaN(parsed)) {
                          setTotalInput("");
                          formik.setFieldValue("totalMissingAmount", "");
                        } else {
                          setTotalInput(getCurrencyFormat(parsed));
                          formik.setFieldValue("totalMissingAmount", parsed);
                        }
                      }}
                    />
                    {errorFor("totalMissingAmount")}
                  </div>

                  {/* Monto por quincena */}
                  <div className="col-md-4">
                    <FieldLabel>Monto por quincena</FieldLabel>
                    <input
                      type="text"
                      style={{ ...inputStyle, background: "#f8fafc", color: "#6b7280" }}
                      value={displayCurrency(formik.values.amountPerPeriod)}
                      disabled
                    />
                  </div>

                  {/* Quincenas */}
                  <div className="col-md-4">
                    <FieldLabel>Quincenas</FieldLabel>
                    <div style={{
                      ...inputStyle, background: "#f8fafc",
                      display: "flex", alignItems: "center", gap: "0.5rem",
                    }}>
                      <i className="fa-solid fa-calendar-days" style={{ fontSize: 12, color: "#9ca3af" }} />
                      <span style={{ color: fortnightsCount ? "#111827" : "#9ca3af", fontWeight: fortnightsCount ? 600 : 400 }}>
                        {fortnightsCount > 0 ? `${fortnightsCount} quincena${fortnightsCount !== 1 ? "s" : ""}` : "—"}
                      </span>
                    </div>
                  </div>

                  {/* Nota */}
                  <div className="col-12">
                    <FieldLabel>Nota</FieldLabel>
                    <textarea
                      style={{ ...inputStyle, resize: "vertical", minHeight: 90, lineHeight: 1.6 }}
                      rows={3}
                      name="note"
                      placeholder="Descripción o comentarios adicionales..."
                      value={formik.values.note}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                </div>
              </ConfigProvider>
            </div>
          </div>

          {/* Archivos */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
            <SectionTitle icon="fa-paperclip">Archivos adjuntos</SectionTitle>
            <div style={{ padding: "1.25rem" }}>
              {/* Drop zone */}
              <div
                style={{
                  border: `2px dashed ${dragOver ? "#3b82f6" : "#d1d5db"}`,
                  borderRadius: 12, padding: "2rem 1rem", textAlign: "center",
                  background: dragOver ? "#eff6ff" : "#f8fafc",
                  cursor: "pointer", transition: "all 0.15s",
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  addFiles(Array.from(e.dataTransfer.files || []));
                }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => document.getElementById("mb-file-upload")?.click()}
              >
                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: 28, color: dragOver ? "#3b82f6" : "#9ca3af", marginBottom: 8 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                  Arrastra archivos aquí o haz clic para seleccionar
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>PNG, JPG, PDF</div>
                <input
                  type="file"
                  multiple
                  id="mb-file-upload"
                  accept=".png,.jpg,.jpeg,.pdf"
                  style={{ display: "none" }}
                  onChange={(e) => addFiles(Array.from(e.target.files || []))}
                />
              </div>

              {/* File list */}
              {(formik.values.missingBoxFiles?.length || 0) > 0 && (
                <div style={{ marginTop: "1rem", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "0.75rem" }}>
                  {formik.values.missingBoxFiles.map((file: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #e5e7eb", borderRadius: 10,
                        overflow: "hidden", background: "#f8fafc", position: "relative",
                      }}
                    >
                      {file.type?.match(/(png|jpg|jpeg)$/) ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }}
                        />
                      ) : (
                        <div style={{
                          height: 90, display: "flex", alignItems: "center",
                          justifyContent: "center", flexDirection: "column", gap: 4,
                        }}>
                          <i className="fa-solid fa-file-pdf" style={{ fontSize: 24, color: "#ef4444" }} />
                          <span style={{ fontSize: 10, color: "#6b7280", textAlign: "center", padding: "0 4px", wordBreak: "break-all" }}>
                            {file.name?.slice(0, 16)}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (formik.values.missingBoxFiles || []).filter((_: any, i: number) => i !== index);
                          formik.setFieldValue("missingBoxFiles", updated);
                        }}
                        style={{
                          position: "absolute", top: 4, right: 4,
                          width: 22, height: 22, borderRadius: "50%",
                          background: "#ef4444", border: "none", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <i className="fa-solid fa-xmark" style={{ fontSize: 10, color: "#fff" }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16,
            padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end", gap: "0.5rem",
          }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={loading}
              style={{
                padding: "0.5rem 1.1rem", borderRadius: 8, fontSize: 13, fontWeight: 500,
                background: "#f8fafc", color: "#374151", border: "1px solid #d1d5db",
                cursor: "pointer",
              }}
            >
              <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => formik.handleSubmit()}
              disabled={loading}
              style={{
                padding: "0.5rem 1.25rem", borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: loading ? "#50B7b1" : "#50B7C0", color: "#fff", border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
              }}
            >
              <i className="fa-solid fa-floppy-disk" style={{ fontSize: 12 }} />
              {loading ? "Guardando..." : "Guardar faltante"}
            </button>
          </div>

        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 768px) {
          .new-missing-box-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default NewMissingBox;
